import React, { useState } from 'react';
import { IconFileDownload } from '@tabler/icons-react';
import { downloadPurchaseOrderPdf } from '../../lib/purchaseOrderPdf';

// Session 78n8.3, Issue #413. The purchase order, produced again after the fact.
//
// THE PROBLEM THIS SOLVES, in plain words. Until this shipped, the purchase
// order Jennifer sends a vendor existed for about thirty seconds. It was drawn
// in her browser, attached to an email, and then gone -- the Mark as ordered
// sheet was the only screen in Stede that could build one, and closing it
// destroyed the only copy. Nobody could answer "what did we actually send
// them": not the manager who approved the spend, not AP holding the vendor's
// bill, not Jennifer an hour later.
//
// WHAT MAKES THE RE-DOWNLOAD TRUSTWORTHY, and it is the whole design. This does
// NOT rebuild the order from the current state of the database. It reads the
// FROZEN COPY written at the moment of sending (material_requests.po_document)
// and hands it to renderPurchaseOrderPdf -- the same generator that built the
// vendor's attachment, over the same input. So the file that comes out is the
// file that went out, and it stays that way after somebody corrects the company
// address, edits the vendor, or fixes a line price. A re-derivation would answer
// "what would we send today", which is a different question and the wrong one:
// the vendor is invoicing against the copy they received.
//
// WHY IT IS ONE COMPONENT AND NOT THREE BUTTONS. It renders on the Purchasing
// Queue (the buyer), the Requests inbox (the approver, which is where the
// "Order placed" email's own link lands) and Invoicing (AP, matching a bill
// against what was ordered -- Colby's addition, and the strongest of the three:
// Invoicing already searches by PO number, so somebody arrives there holding
// exactly the reference this document is filed under). Three copies of a
// download that must produce an identical file is three chances for one of them
// to drift.

// COLOUR, Colby's ask mid-session: *"can we make the download button a slightly
// different color than the background so it stands out a little?"* It was a
// white button on a near-white panel and read as furniture.
//
// The tint is the PURCHASING accent (#EF9F27, locked in CLAUDE.md) at low
// saturation, not a new colour invented for this control — a purchase order is a
// purchasing document wherever it is being read from, including the Requests
// inbox and Invoicing. #8A5A08 is the same darkened amber the Purchasing Queue
// already uses for its "received, outstanding" emphasis, so the pair is
// borrowed rather than introduced.
//
// Deliberately still a SECONDARY weight — tinted fill, no solid block. It sits
// beside "Mark received" and "Enter invoice", which are the actions that change
// something; this one only hands over a file, and giving it equal weight would
// pull the eye away from the decision the reader is actually there to make.
const BASE = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  // These three are InvoicingPage.js's existing `amber` token verbatim
  // (bg #FFF8EC, fg #8A5A08, br #F3D9A8), not a fresh mix. A first pass here
  // landed two or three hex points off it by coincidence, which is how an app
  // ends up with four ambers nobody chose. Same reasoning as copying the search
  // predicate rather than writing a fourth variation of it.
  border: '1px solid #F3D9A8', background: '#FFF8EC', color: '#8A5A08',
  borderRadius: 7, fontSize: 11, fontWeight: 600, padding: '6px 10px',
  cursor: 'pointer', whiteSpace: 'nowrap',
};

// The "there is no copy" case, which every order placed before this session is
// in. It is a sentence rather than a disabled button, because a greyed-out
// Download reads as "you lack permission" or "it is still loading" -- both of
// which invite waiting or asking an administrator. Nothing is wrong and nothing
// can be done; the honest thing is to say so and stop.
//
// Deliberately NOT rendered for requests that were never ordered: the caller
// decides that, because only the caller knows the status.
function NoCopyNote() {
  return (
    <div style={{ fontSize: 11, color: '#9A9A9A', fontStyle: 'italic' }}>
      This order was placed before Stede kept a copy of the purchase order.
    </div>
  );
}

export default function PurchaseOrderDownload({ poDocument, label = 'Download PO' }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!poDocument) return <NoCopyNote />;

  async function handleDownload() {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await downloadPurchaseOrderPdf(poDocument);
    } catch (e) {
      // Surfaced rather than swallowed. Unlike the SEND path -- where a thrown
      // error invites pressing Confirm again and creating a second order --
      // nothing here has any side effect at all, so a failure is safe to report
      // plainly and safe to retry.
      console.error('[#413] PO re-download failed', e);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        style={{ ...BASE, opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}
      >
        <IconFileDownload size={12} color="#8A5A08" />
        {busy ? 'Building…' : label}
      </button>
      {failed && (
        <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>
          The PDF could not be built. The order itself is unaffected — try again.
        </div>
      )}
    </div>
  );
}
